/**
 * NotificationCTA Component
 *
 * A simple, clean CTA to encourage users to enable notifications.
 * Based on the original SmartSummaryPage design.
 */

import { FC, useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { posthog } from 'posthog-js';
import { colors, spacing, fontStyles } from '../../styles';
import { useNotificationPreferences } from '../../hooks/useNotificationPreferences';
import NotificationBlockedModal from '../NotificationBlockedModal';

const STORAGE_KEY = 'notificationCTADismissed';

export const NotificationCTA: FC = () => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [hasTrackedShow, setHasTrackedShow] = useState(false);
  const {
    areAllEnabled,
    handleToggleAll,
    isNotificationBlockedModalOpen,
    setIsNotificationBlockedModalOpen,
    isSyncing,
  } = useNotificationPreferences();

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed === 'true') setIsDismissed(true);
  }, []);

  // Listen for cross-tab storage events to sync dismissal state
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue === 'true') {
        setIsDismissed(true);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Track when CTA is shown
  useEffect(() => {
    if (!hasTrackedShow && !areAllEnabled && !isDismissed) {
      posthog.capture('notification_cta_shown', { location: 'ideas_page' });
      setHasTrackedShow(true);
    }
  }, [hasTrackedShow, areAllEnabled, isDismissed]);

  const handleDismiss = () => {
    posthog.capture('notification_cta_dismissed', { location: 'ideas_page' });
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsDismissed(true);
  };

  const handleEnableClick = async () => {
    posthog.capture('notification_cta_clicked', { location: 'ideas_page' });
    await handleToggleAll();
  };

  // Hide if notifications already enabled or dismissed
  if (areAllEnabled || isDismissed) return null;

  return (
    <>
      <div
        css={css`
          background: ${colors.snow};
          border: 0.5px solid ${colors.cloud};
          border-radius: 16px;
          padding: ${spacing.regular};
          margin-bottom: ${spacing.regular};
          display: flex;
          flex-direction: column;
          position: relative;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

          html[data-theme='dark'] & {
            background: #242424;
            border-color: rgba(255, 255, 255, 0.06);
            box-shadow: none;
          }
        `}
      >
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.centi};
              margin-bottom: ${spacing.micro};
            `}
          >
            <span
              css={css`
                font-size: 24px;
                flex-shrink: 0;
              `}
            >
              🔔
            </span>
            <div
              css={css`
                ${fontStyles.dog}
                font-weight: 500;
                color: ${colors.liquorice};
                flex: 1;

                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              Get daily market updates
            </div>
            <button
              onClick={handleDismiss}
              aria-label="Dismiss notification CTA"
              css={css`
                background: none;
                border: none;
                color: ${colors.silver};
                cursor: pointer;
                padding: ${spacing.micro};
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                transition: all 0.2s ease;
                margin-left: auto;
                font-size: 16px;

                &:hover {
                  background: rgba(0, 0, 0, 0.05);
                  color: ${colors.tungsten};
                }

                html[data-theme='dark'] & {
                  &:hover {
                    background: rgba(255, 255, 255, 0.05);
                    color: ${colors.silver};
                  }
                }
              `}
            >
              ✕
            </button>
          </div>
          <div
            css={css`
              ${fontStyles.cat}
              color: ${colors.tungsten};

              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            Stay on top of markets with your personal daily update
          </div>
        </div>
        <button
          onClick={handleEnableClick}
          disabled={isSyncing}
          css={css`
            background: transparent;
            border: 1px solid ${colors.petrolBlue};
            border-radius: 8px;
            padding: ${spacing.centi} ${spacing.regular};
            ${fontStyles.cat}
            color: ${colors.petrolBlue};
            cursor: pointer;
            transition: all 0.2s ease;
            width: 100%;

            &:hover {
              background: rgba(17, 131, 131, 0.08);
            }

            &:disabled {
              opacity: 0.7;
              cursor: not-allowed;
            }

            html[data-theme='dark'] & {
              border-color: ${colors.skyBlue};
              color: ${colors.skyBlue};

              &:hover {
                background: rgba(40, 181, 189, 0.1);
              }
            }
          `}
        >
          {isSyncing ? 'Enabling...' : 'Enable notifications'}
        </button>
      </div>
      <NotificationBlockedModal
        isOpen={isNotificationBlockedModalOpen}
        onClose={() => setIsNotificationBlockedModalOpen(false)}
      />
    </>
  );
};

export default NotificationCTA;
